import { routes } from "@/lib/routes";

/**
 * Funnel route detection.
 *
 * The conversion funnels (schnellangebot, schnellanfrage, flaechen-pruefen)
 * live under the `app/(funnel)` route group. They render their own chrome
 * (FunnelHeader + a bottom-anchored primary CTA), so the global Header and
 * PageFooter must never appear on these routes.
 *
 * The funnel already hides that global chrome via the `useHideHeader` /
 * `useHideFooter` effects (see `Providers`), but those run only *after*
 * hydration. `Providers` uses this predicate to also keep the chrome out of
 * the server-rendered HTML, so it is never briefly painted — or clickable —
 * during the pre-hydration window. That window is where a tapped footer legal
 * link (AGB/Datenschutz) or global-nav link would navigate the whole funnel
 * away and lose the user's entered lead.
 */

/**
 * Root URL prefix of a funnel route (its first path segment) —
 * `/schnellangebot/standort` → `/schnellangebot`, `/flaechen-pruefen` →
 * `/flaechen-pruefen`. Deriving the roots from `routes.ts` (rather than
 * re-hardcoding the slugs) keeps them in sync: rename a funnel slug in the
 * central registry and the prefix follows automatically, so the chrome-hiding
 * gate can't silently desync. See `.claude/rules/routes.md`.
 */
const funnelRoot = (routePath: string): string => `/${routePath.split("/")[1] ?? ""}`;

/** URL prefixes served by the `app/(funnel)` route group, sourced from `routes`. */
export const FUNNEL_PATH_PREFIXES = [
  funnelRoot(routes.schnellangebot),
  funnelRoot(routes.schnellanfrage),
  funnelRoot(routes.flaechenPruefen),
] as const;

/**
 * True when `pathname` is a conversion-funnel route — either a funnel root or
 * a sub-path like `/schnellangebot/kontakt`. A different route that merely
 * shares the leading characters (e.g. `/schnellangebote`) does NOT match.
 */
export function isFunnelPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return FUNNEL_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}
